import { getConfig, getErrorMessage, klinikenApi } from "@/api";
import PatientRegistration from "@/tabs/Patient/PatientRegistration.vue";
import { flashMessage, flashWarning, openDialog } from "@/utils";
import { selectPatient } from "@/utils/patient/selectPatient";
import eq from "lodash/eq";
import { AxiosError } from "axios";
import { useStore } from "@/store";

export const externalPatientDataLabels = {
    personId: "PersonID",
    lan: "Län",
    adressRad1: "Addressrad 1",
    adressRad2: "Addressrad 2",
    tilltalsnamn: "Tilltalsnamn",
    fornamn: "Förnamn",
    efternamn: "Efternamn",
    careCoverage: "Förmånsberättigad",
    avliden: "Avliden",
    skyddad_identitet: "Skyddad folkbokföring",
    sekretess: "Sekretessmarkering",
    postort: "Postort",
    postnummer: "Postnummer",
    coAdress: "C/O-adress",
    dateOfBirth: "Födelsedatum",
    gender: "Kön",
};

export const updatePurExternalPatientData = async ({
    personId,
    currentPatientData,
    action,
    tabId,
}: {
    personId: string;
    currentPatientData: any;
    action: "edit" | "create";
    tabId: string;
}): Promise<any | undefined> => {
    const store = useStore();

    try {
        const method = action === "create" ? "POST" : "PUT";
        const flashTime = 5000;

        const response = await klinikenApi(
            getConfig({
                method,
                url: action === "create" ? "core/patienter/pur/" : `core/patienter/pur/${currentPatientData.id}/`,
                data: {
                    personId, // Hårdkoda för att det bara är personnummer vi stödjer för uppslag i PUR
                    personIdType: "1.2.752.129.2.1.3.1",
                },
            })
        );

        const newData = {
            ...response.data,
            // Konvertera t.ex strängen 01 till 1 för att matcha vårt system
            lan: response.data.lan ? Number(response.data.lan) : undefined,
        };

        if (action === "edit") {
            const diffLabels = Object.keys(newData).reduce((base, key) => {
                if (key.includes("_at") || key.includes("_by") || eq(newData[key], currentPatientData[key])) {
                    return base;
                }

                const label = externalPatientDataLabels[key as keyof typeof externalPatientDataLabels];

                if (label) {
                    return [...base, label];
                }

                return base;
            }, [] as Array<String>);

            const flashText =
                diffLabels.length > 0
                    ? `Uppdaterat personuppgifter (${diffLabels.length}): ${diffLabels.join(", ")}`
                    : "Inga personuppgifter har ändrats sendan senaste uppdateringen";

            store.commit("patientData/load", response.data);

            flashMessage(flashText, flashTime);
        } else {
            flashMessage("Personuppgifter hämtade", flashTime);
        }

        return newData;
    } catch (error) {
        const responseStatus = (error as AxiosError)?.response?.status;

        if (responseStatus === 400) {
            flashWarning("Kunde inte hämta personuppgifter. Kontrollera att PersonID och Typ av PersonID stämmer");
        } else if (responseStatus === 404) {
            flashWarning("Hittade ingen person som matchade. Kontrollera att PersonID och Typ av PersonID stämmer");
        } else if (responseStatus === 409) {
            openDialog({
                type: "info",
                description: `Patienten ${personId} finns redan. Vill du gå vidare och välja patienten?`,
                buttons: [
                    {
                        title: "Avbryt",
                        type: "secondary",
                        action: () => {},
                    },
                    {
                        title: "Ja, välj patient",
                        type: "primary",
                        action: async () => {
                            const patientId = await selectPatient({ personId });

                            await store.dispatch("tabs/closeTab", tabId);
                            await store.commit("patientData/setEditPatient", patientId);
                            await store.dispatch("tabs/openTab", {
                                component: PatientRegistration,
                            });
                        },
                    },
                ],
            });
        } else {
            openDialog("Kunde inte hämta patient från servern." + getErrorMessage(error), "error");
        }
    }
};
